<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { messageStamp } from 'shared/helpers/timeHelper';

import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import CsatExpandedRow from './CsatExpandedRow.vue';
import CsatEmptyState from './CsatEmptyState.vue';
import CsatTableLoader from './CsatTableLoader.vue';

import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { pageIndex, pageSize } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number,
    default: 25,
  },
});

const emit = defineEmits(['pageChange', 'pageSizeChange']);
const { t } = useI18n();
const router = useRouter();
const { accountId, isCloudFeatureEnabled, isOnChatwootCloud } = useAccount();
const csatResponses = useMapGetter('csat/getCSATResponses');
const meta = useMapGetter('csat/getMeta');

const isFeatureEnabled = computed(() =>
  isCloudFeatureEnabled('csat_review_notes')
);
const showExpandableRows = computed(
  () =>
    (isFeatureEnabled.value || isOnChatwootCloud.value) &&
    csatResponses.value.some(row => row.id)
);
const uiFlags = useMapGetter('csat/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);

const expandedRows = ref({});

const toggleRow = id => {
  if (!id) return;
  expandedRows.value = {
    ...expandedRows.value,
    [id]: !expandedRows.value[id],
  };
};

const isRowExpanded = id => !!expandedRows.value[id];

const formatUtc = value => (value ? messageStamp(value, 'dd/MM HH:mm') : '—');

const tableData = computed(() =>
  csatResponses.value.map(response => ({
    id: response.id || `message-${response.message_id}`,
    responseId: response.id,
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    feedbackText: response.feedback_message || '',
    displayType: response.display_type,
    conversationId: response.conversation_id,
    csatReviewNotes: response.csat_review_notes,
    surveySentAt: formatUtc(response.survey_sent_at),
    responseAt: formatUtc(response.response_at),
    resolvedBy: response.resolved_by_name || '—',
    teamName: response.team_name || '—',
    inboxName: response.inbox_name || '—',
    labels: Array.isArray(response.labels) ? response.labels.join(', ') : '—',
    responded: response.responded,
    _original: response,
  }))
);

const getRatingData = rating => {
  return CSAT_RATINGS.find(r => r.value === rating) || {};
};

const getRatingText = row => {
  if (!row.responded || row.rating == null) {
    return t('CSAT_REPORTS.FILTERS.RATINGS.NO_RESPONSE');
  }
  if (row.displayType === 'like_dislike') {
    return row.rating === 5 ? 'good' : 'bad';
  }
  return t(getRatingData(row.rating).translationKey);
};

const openConversation = conversationId => {
  if (!conversationId) return;
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversationId },
  });
};

const columnHelper = createColumnHelper();

const columns = computed(() => {
  const baseColumns = [
    columnHelper.accessor('surveySentAt', {
      header: t('CSAT_REPORTS.TABLE.HEADER.SURVEY_SENT'),
      size: 140,
    }),
    columnHelper.accessor('responseAt', {
      header: t('CSAT_REPORTS.TABLE.HEADER.RESPONSE_AT'),
      size: 140,
    }),
    columnHelper.accessor('rating', {
      header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
      size: 120,
    }),
    columnHelper.accessor('feedbackText', {
      header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
      size: 280,
    }),
    columnHelper.accessor('resolvedBy', {
      header: t('CSAT_REPORTS.TABLE.HEADER.RESOLVED_BY'),
      size: 140,
    }),
    columnHelper.accessor('teamName', {
      header: t('CSAT_REPORTS.TABLE.HEADER.TEAM'),
      size: 120,
    }),
    columnHelper.accessor('inboxName', {
      header: t('CSAT_REPORTS.TABLE.HEADER.SOURCE'),
      size: 120,
    }),
    columnHelper.accessor('labels', {
      header: t('CSAT_REPORTS.TABLE.HEADER.TAGS'),
      size: 140,
    }),
    columnHelper.accessor('conversationId', {
      header: t('CSAT_REPORTS.TABLE.HEADER.OPEN_DIALOGUE'),
      size: 100,
    }),
  ];

  if (showExpandableRows.value) {
    baseColumns.push(
      columnHelper.accessor('actions', {
        header: '',
        size: 50,
      })
    );
  }

  return baseColumns;
});

const paginationParams = computed(() => ({
  pageIndex,
  pageSize,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return meta.value.count || 0;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    if (newPagination.pageSize !== pageSize) {
      emit('pageSizeChange', newPagination.pageSize);
    }
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-hidden"
  >
    <div class="border-b border-n-weak px-6 py-4">
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ $t('CSAT_REPORTS.TABLE.TITLE') }}
      </h3>
    </div>

    <CsatTableLoader v-if="isLoading" />

    <div v-else-if="tableData.length">
      <table class="w-full table-fixed">
        <thead class="bg-n-solid-2 border-b border-n-container">
          <tr>
            <th
              class="w-[9%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.SURVEY_SENT') }}
            </th>
            <th
              class="w-[9%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.RESPONSE_AT') }}
            </th>
            <th
              class="w-[8%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.RATING') }}
            </th>
            <th
              class="w-[18%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT') }}
            </th>
            <th
              class="w-[14%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.RESOLVED_BY') }}
            </th>
            <th
              class="w-[12%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.TEAM') }}
            </th>
            <th
              class="w-[10%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.SOURCE') }}
            </th>
            <th
              class="w-[12%] text-left py-2.5 px-2 font-medium text-xs text-n-slate-12"
            >
              {{ $t('CSAT_REPORTS.TABLE.HEADER.TAGS') }}
            </th>
            <th
              class="w-[8%] text-right py-2.5 px-2 font-medium text-xs text-n-slate-12"
            />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-container">
          <template v-for="row in tableData" :key="row.id">
            <tr
              class="group hover:bg-n-slate-2 dark:hover:bg-n-solid-3 transition-colors"
              :class="{
                'bg-n-slate-2 dark:bg-n-solid-3': isRowExpanded(row.responseId),
                'cursor-pointer': showExpandableRows && row.responseId,
              }"
              @click="showExpandableRows && toggleRow(row.responseId)"
            >
              <td
                class="py-2.5 px-2 text-xs tabular-nums text-n-slate-12 whitespace-nowrap"
              >
                {{ row.surveySentAt }}
              </td>
              <td
                class="py-2.5 px-2 text-xs tabular-nums text-n-slate-12 whitespace-nowrap"
              >
                {{ row.responseAt }}
              </td>
              <td class="py-2.5 px-2">
                <div
                  class="inline-flex max-w-full items-center px-1.5 py-0.5 rounded-md"
                  :style="{
                    backgroundColor: row.responded
                      ? `${getRatingData(row.rating).color}20`
                      : undefined,
                  }"
                >
                  <span class="text-xs font-medium text-n-slate-12 truncate">
                    {{ getRatingText(row) }}
                  </span>
                </div>
              </td>
              <td class="py-2.5 px-2 min-w-0">
                <span
                  v-if="!row.feedbackText"
                  class="text-n-slate-10 italic text-xs"
                >
                  {{ $t('CSAT_REPORTS.NO_FEEDBACK') }}
                </span>
                <div
                  v-else
                  class="text-xs text-n-slate-12 truncate"
                  :title="row.feedbackText"
                >
                  {{ row.feedbackText }}
                </div>
              </td>
              <td class="py-2.5 px-2 min-w-0">
                <div class="truncate text-xs text-n-slate-12">
                  <UserAvatarWithName
                    v-if="row.assignedAgent"
                    :user="row.assignedAgent"
                    :size="20"
                  />
                  <span v-else>{{ row.resolvedBy }}</span>
                </div>
              </td>
              <td
                class="py-2.5 px-2 text-xs text-n-slate-11 truncate"
                :title="row.teamName"
              >
                {{ row.teamName }}
              </td>
              <td
                class="py-2.5 px-2 text-xs text-n-slate-11 truncate"
                :title="row.inboxName"
              >
                {{ row.inboxName }}
              </td>
              <td
                class="py-2.5 px-2 text-xs text-n-slate-11 truncate"
                :title="row.labels"
              >
                {{ row.labels }}
              </td>
              <td class="py-2.5 px-2 text-right whitespace-nowrap">
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-md p-1.5 text-n-brand transition hover:bg-n-alpha-2"
                  :title="$t('CSAT_REPORTS.TABLE.OPEN')"
                  @click.stop="openConversation(row.conversationId)"
                >
                  <span class="i-lucide-arrow-up-right size-3.5" />
                </button>
                <button
                  v-if="showExpandableRows && row.responseId"
                  type="button"
                  class="inline-flex items-center justify-center rounded-md p-1.5 text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                  @click.stop="toggleRow(row.responseId)"
                >
                  <i
                    class="size-3.5 block"
                    :class="
                      isRowExpanded(row.responseId)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                  />
                </button>
              </td>
            </tr>
            <tr
              v-if="
                showExpandableRows &&
                row.responseId &&
                isRowExpanded(row.responseId)
              "
              class="!border-t-0"
            >
              <td colspan="9" class="p-0 !border-t-0">
                <CsatExpandedRow :response="row._original" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <CsatEmptyState
      v-else
      :title="$t('CSAT_REPORTS.NO_RECORDS')"
      :description="$t('CSAT_REPORTS.NO_RECORDS_DESCRIPTION')"
    />

    <div v-if="meta.count" class="px-6 py-4 border-t border-n-weak">
      <Pagination
        :table="table"
        show-page-size-selector
        :default-page-size="pageSize"
        @page-size-change="size => emit('pageSizeChange', size)"
      />
    </div>
  </div>
</template>
